import * as yaml from 'js-yaml';

import {CanUninstallResponse, CmsProjectWithInstances, SubmitFormDataResponse, ZipApi} from '../lib/ZipApi';
import {computed, action as mobxAction, observable, reaction} from 'mobx';
import {showToasts, toaster} from '../lib/toaster';
import {getApiErrorMessage} from '../lib/ZipApiError';

import {AppInstallation} from '../types/AppInstallation';
import {AppLogEntry} from '../types/AppLogEntry';
import {AppVersion} from '../types/AppVersion';
import {AppVersionMetadata} from '../types/AppVersionMetadata';
import {HostPageMessenger} from '../lib/HostPageMessenger';
import {Intent} from '@blueprintjs/core';
import {Schema} from '@zaiusinc/app-forms-schema';
import {directoryStore} from './DirectoryStore';
import {subHours} from 'date-fns';
import {AppFeature, InstallationScopeType, OPAL_CATEGORY} from '../types/AppConstants';

const ZAIUS_AUTH_URI: string = import.meta.env.VITE_ZAIUS_AUTH_URI!;

const IFRAME_PARENT_ORIGIN: string = import.meta.env.VITE_IFRAME_PARENT_ORIGIN || (() => {
  const url = new URL(window.location.href);
  const stem = url.host.split('.').slice(1);
  if (stem[stem.length - 2] === 'zaius') {
    url.host = 'app.zaius.com';
    if (stem[0] === 'staging') {
      url.host = 'staging.zaius.com';
    }
    return url.origin;
  }
  stem[1] = 'odp';
  url.host = ['app'].concat(stem).join('.');
  return url.origin;
})();

export interface HandleActionResponse {
  success: boolean;
  redirectMode?: 'url' | 'settings';
  redirectTarget?: string;
  toasts?: Array<{
    intent: Intent | 'info' | 'success' | 'warning' | 'danger';
    message: string;
  }>;
  hasErrors?: boolean;
}

const LOG_PAGE_SIZE = 200;
export interface AppLogFilter {
  search?: string;
  level?: string;
  startDate?: Date;
  endDate?: Date;
}

export class AppDetailStore {
  @observable public loading = false;
  @observable public status?: number;
  @observable public version?: string;
  @observable public detail?: AppVersionMetadata;
  /** The account's single installation. Per-instance apps use `installations` instead. */
  @observable private accountInstall?: AppInstallation;
  @observable public settingsFormSchema?: Schema.Form;
  @observable public settingsFormData?: Schema.FormData;
  @observable public settingsFormErrors = {sections: observable.map<string, string[]>({})};
  @observable public installing = false;
  @observable public uninstalling = false;
  @observable public scopeLoading = false;
  @observable public moreLogLoaded = false;
  @observable public logLoaded = false;
  @observable public allAppLogs: AppLogEntry[] = [];
  @observable public logFilter: AppLogFilter = {startDate: subHours(new Date(), 1), endDate: new Date()};
  @observable public cursor_token?: string;
  @observable public hasMoreLogs = true;
  @observable public app_features: AppFeature[] = [];
  @observable public hasCmsInstallationScope = false;
  @observable public selectedCmsScope?: string;
  @observable public cmsProjects: CmsProjectWithInstances[] = [];
  @observable public cmsProjectsLoading = false;
  /** This app's installations in the account — one per CMS instance for a per-instance app. */
  @observable public installations: AppInstallation[] = [];
  @observable public latestRunningVersion?: string | null;

  constructor(public appId: string, public requestedVersion?: string) {
    this.version = requestedVersion;
    reaction(() => directoryStore.trackerId, () => this.fetch(), {fireImmediately: true, delay: 100});
  }

  /**
   * The installation the page is acting on: the one on the selected CMS instance for a
   * per-instance app, the account's single installation otherwise. Everything install-keyed
   * reads through this, so switching instances re-points the whole page.
   */
  public get activeInstall(): AppInstallation | undefined {
    if (this.hasCmsInstallationScope) {
      return this.selectedCmsScope ? this.installationForScope(this.selectedCmsScope) : undefined;
    }
    return this.accountInstall;
  }

  public get installId(): number | undefined {
    return this.activeInstall?.id;
  }

  public get installedVersion(): string | undefined {
    return this.activeInstall?.version;
  }

  public get installed() {
    return !!this.installId;
  }

  public get sameVersionInstalled() {
    return !!this.installId && (this.installedVersion === this.version);
  }

  public get hasTroubleshooting() {
    const vendor = this.detail?.vendor.toLocaleLowerCase();
    return this.sameVersionInstalled && vendor !== 'zaius' && vendor !== 'optimizely';
  }

  public get hasOpalCategory() {
    return this.detail?.categories.includes(OPAL_CATEGORY) ?? false;
  }

  public get hasOpal() {
    return !!this.installId && this.app_features.includes(AppFeature.OPAL_TOOL_APP);
  }

  public get hasCMSUIExtensions() {
    return !!this.installId && this.app_features.includes(AppFeature.CMS_UI_EXTENSION_APP);
  }

  /** This app's installation on the given CMS instance, or undefined if it isn't installed there. */
  public installationForScope(scope: string): AppInstallation | undefined {
    return this.installations.find((i) => i.scope === scope);
  }

  public get selectedCmsInstanceName(): string | undefined {
    if (!this.selectedCmsScope) {
      return undefined;
    }
    for (const project of this.cmsProjects) {
      const inst = project.instances.find((i) => i.id === this.selectedCmsScope);
      if (inst) {
        return inst.name;
      }
    }
    return this.selectedCmsScope;
  }

  public async install(): Promise<boolean> {
    // Only ever set for a per-instance app, so it doubles as "install onto this CMS instance"
    const scope = this.selectedCmsScope;
    if (this.installed || !directoryStore.trackerId || (this.hasCmsInstallationScope && !scope)) {
      return false;
    }
    this.installing = true;
    try {
      // A new CMS instance gets the version a new install gets; for an app that is not PUBLIC the grant decides anyway
      const version = scope ? this.latestRunningVersion || undefined : this.requestedVersion;
      await ZipApi.install(directoryStore.trackerId, this.appId, version, scope);
      await this.fetch();
      this.sendJumbeEvent('install_success');
      return true;
    } catch (e: any) {
      const message = await getApiErrorMessage(e, 'Unable to install app');
      showToasts([{intent: 'danger', message}]);
      this.installing = false;
      this.sendJumbeEvent('install_failure');
      return false;
    }
  }

  @mobxAction('AppDetailStore<SelectCmsScope>')
  public async selectCmsScope(scope: string | undefined) {
    this.selectedCmsScope = scope;
    this.settingsFormSchema = undefined;
    this.settingsFormData = undefined;
    this.settingsFormErrors.sections.clear();
    if (!scope) {
      return;
    }
    this.scopeLoading = true;
    this.version = this.versionForScope(scope) ?? undefined;
    await this.fetchVersionForScope(scope);
    if (this.version) {
      this.getSettingsFormSchema();
      this.getSettingsFormData();
    }
    this.scopeLoading = false;
  }

  /**
   * Loads a version's metadata into the page. A null/undefined version means there is nothing
   * to show — an uninstalled instance of an app with no stable version to install.
   */
  private async loadVersionMetadata(version: string | null | undefined) {
    if (!version) {
      this.version = undefined;
      return;
    }
    const result = await ZipApi.fetchAppVersion(this.appId, version);
    this.detail = result.app_version.metadata;
    this.version = result.app_version.id.version;
    this.app_features = result.app_version.features || [];
  }

  /** The version to show for a CMS instance: the one installed on it, else the one a new install gets. */
  private versionForScope(scope: string | undefined): string | null | undefined {
    const installedVersion = scope ? this.installationForScope(scope)?.version : undefined;
    return installedVersion || this.latestRunningVersion;
  }

  private async fetchVersionForScope(scope: string) {
    try {
      await this.loadVersionMetadata(this.versionForScope(scope));
    } catch (error: any) {
      const message = await getApiErrorMessage(error, 'Unable to load app version');
      showToasts([{intent: 'danger', message}]);
    }
  }

  public async loadCmsProjects() {
    if (!directoryStore.trackerId) {
      return;
    }
    this.cmsProjectsLoading = true;
    try {
      this.cmsProjects = await ZipApi.listCMSUIExtensionProjectsV2(directoryStore.trackerId);
    } catch (e: any) {
      const message = await getApiErrorMessage(e, 'Unable to load CMS instances');
      showToasts([{intent: 'danger', message}]);
    } finally {
      this.cmsProjectsLoading = false;
    }
  }

  public async canUninstall(): Promise<CanUninstallResponse> {
    const installId = this.installId;
    if (installId) {
      // Check if the app has CMS UI extensions enabled
      const cmsUIExtensionCheck = await this.canUninstallCMSUIExtensionApp();
      if (cmsUIExtensionCheck && !cmsUIExtensionCheck.uninstallable) {
        return cmsUIExtensionCheck;
      }

      // Also blocks while the app's tools are added to Mark (in any account, not only the user's), then runs
      // the app's canUninstall lifecycle hook
      return await ZipApi.canUninstall(installId);
    } else {
      throw new Error(`Install id is not set`);
    }
  }

  public async uninstall(): Promise<boolean> {
    const scope = this.selectedCmsScope;
    if (!this.installed || !directoryStore.trackerId) {
      return false;
    }
    this.uninstalling = true;
    try {
      await ZipApi.uninstall(directoryStore.trackerId, this.appId, scope);
      await this.fetch();
      this.sendJumbeEvent('uninstall_success');
      return true;
    } catch (e: any) {
      console.error(e);
      this.uninstalling = false;
      let message = 'Unable to perform uninstall at this time.  Please try again.';
      if (e.response && e.response.status && e.response.status === 400) {
        try {
          message = (await e.response.json()).message;
        } catch (e) {
          // eating this just in case there was a problem parsing the body as json
        }

      } else {
        this.sendJumbeEvent('uninstall_failure');
      }

      toaster.show({message, intent: Intent.DANGER});
      return false;
    }
  }

  public async handleAction(
    section: string, action: string, data: Schema.FormSectionData): Promise<HandleActionResponse> {
    return action === 'oauth'
      ? await this.authorizationRequest(section, data)
      : await this.submitFormSection(section, action, data);
  }

  public processErrors(errors: SubmitFormDataResponse['response_json']['errors']) {
    const errorsBySection: Schema.FormErrors['sections'] = {};
    for (const key of Object.keys(errors)) {
      const [sectionKey, field] = key.split('.');
      if (!errorsBySection[sectionKey]) {
        errorsBySection[sectionKey] = {};
      }
      errorsBySection[sectionKey][field] = errors[key];
    }
    this.settingsFormErrors.sections.replace(errorsBySection);
  }

  public sendJumbeEvent(action: string, data: any = {}) {
    HostPageMessenger.send('event', {
      type: 'app',
      action,
      zip_app_id: this.appId,
      zip_app_version: this.version,
      ...data
    });
  }

  public async fetchLogs() {
    this.logLoaded = false;
    this.moreLogLoaded = true;
    const criteria = this.buildCreteria();
    const payload = {
      criteria,
    };
    try {
      const logEntries = await ZipApi.listAppLogEntries(JSON.stringify(payload));
      this.allAppLogs = logEntries.log_entry || [];
      this.cursor_token = logEntries.cursor_token;
      this.hasMoreLogs = this.allAppLogs.length === LOG_PAGE_SIZE;
    } catch (error: any) {
      const message = await getApiErrorMessage(error, 'Unable to load logs');
      showToasts([{intent: 'danger', message}]);
    } finally {
      this.logLoaded = true;
    }
  }

  public async fetchMore() {
    if (this.version) {
      this.moreLogLoaded = false;
      this.logLoaded = true;
      const criteria = this.buildCreteria();
      const payload = {
        criteria,
        cursor_token: this.cursor_token
      };
      try {
        const logEntries = (await ZipApi.listAppLogEntries(JSON.stringify(payload)));
        this.allAppLogs = this.allAppLogs.concat(logEntries.log_entry || []);
        this.cursor_token = logEntries.cursor_token;
        this.hasMoreLogs = logEntries.hasOwnProperty('log_entry') &&  logEntries.log_entry.length === LOG_PAGE_SIZE;
      } catch (error: any) {
        const message = await getApiErrorMessage(error, 'Unable to load more logs');
        showToasts([{intent: 'danger', message}]);
      } finally {
        this.moreLogLoaded = true;
      }
    }
  }

  @computed
  public get appLogs(): AppLogEntry[] {
    return this.allAppLogs.filter((logEntry) => {
      let keep = true;
      if (this.logFilter.search) {
        const text = logEntry.message;
        keep = keep && this.logFilter.search.toLocaleLowerCase().split(/\W/g).every(
          (term) => text.toLowerCase().includes(term)
        );
      }
      if (this.logFilter.level && this.logFilter.level.toLocaleLowerCase() !== 'all') {
        keep = keep && logEntry.level.toLowerCase() === this.logFilter.level.toLowerCase();
      }
      return keep;
    });
  }

  @computed
  public get appLogsContent(): string {
    return this.appLogs.map((entry) => {
      return this.formatAppLogLine(entry);
    }).join('\n');
  }

  public setLogFilter(filter: AppLogFilter) {
    this.logFilter = filter;
    this.fetchLogs();
  }

  private async canUninstallCMSUIExtensionApp(): Promise<CanUninstallResponse | null> {
    if (!this.hasCMSUIExtensions || !this.installId || this.hasCmsInstallationScope) {
      return null;
    }

    try {
      const enabledInstanceIds = await ZipApi.listCMSUIExtensionEnabledInstances(this.installId);

      if (enabledInstanceIds.length > 0) {
        return {
          uninstallable: false,
          message: 'You cannot uninstall the app while UI extensions are enabled. Please disable them first in the app Settings > Add to CMS section.'
        };
      }
    } catch (error) {
      return {
        uninstallable: false,
        message: 'Unable to verify CMS UI extensions status. Please try again or contact support.'
      };
    }

    return null;
  }

  private buildCreteria() {
    const term = this.logFilter.search;
    return {
      context: {
        app_id: this.appId,
        version: this.version,
        trackerId: directoryStore.trackerId!
      },
      search_strings: term == null ? [] : [term],
      level: this.logFilter.level?.toUpperCase(),
      time_frame: {
        start: this.logFilter.startDate?.toISOString(),
        end: this.logFilter.endDate?.toISOString()
      },
      page_size: LOG_PAGE_SIZE
    };
  }

  private formatAppLogLine(appLogEntry: AppLogEntry) {
    return `${appLogEntry.time} ${appLogEntry.level} ${appLogEntry.message}`;
  }

  private async authorizationRequest(section: string, data: Schema.FormSectionData): Promise<HandleActionResponse> {
    const payload = {
      form_page: section,
      form_data_json: JSON.stringify(data),
      target_url: `${IFRAME_PARENT_ORIGIN}/app${directoryStore.prismContext ? '?scope=' + directoryStore.prismContext.scope : ''}`
    };
    const result = await ZipApi.authorizationRequest(this.installId!, JSON.stringify(payload));
    const response: HandleActionResponse = {success: true};
    if (result.response_json) {
      const {errors, toasts, redirectMode} = result.response_json;
      if (toasts.length !== 0) {
        showToasts(toasts);
      } else if (Object.keys(errors).length) {
        this.processErrors(errors);
      } else if (redirectMode === 'url') {
        const params = [
          `tracker=${directoryStore.trackerId!}`,
          `request_id=${result.auth_request_id}`,
        ];
        const targetUrl = `${ZAIUS_AUTH_URI}/${this.appId}?${params.join('&')}`;
        response.redirectMode = 'url';
        response.redirectTarget = targetUrl;
      }

      // Include toasts and error information in response
      response.toasts = toasts;
      response.hasErrors = errors && Object.keys(errors).length > 0;
    } else {
      this.settingsFormErrors.sections.clear();
      response.hasErrors = false;
    }
    return response;
  }

  private async submitFormSection(
    section: string, action: string, data: Schema.FormSectionData): Promise<HandleActionResponse> {
    const payload = {
      form_page: section,
      action,
      form_data_json: JSON.stringify(data)
    };
    const result = await ZipApi.submitFormData(this.installId!, JSON.stringify(payload));
    const response: HandleActionResponse = {success: true};

    if (result.response_json) {
      const {errors, toasts} = result.response_json;
      if (toasts.length === 0) {
        toasts.push({intent: 'success', message: 'Updated Settings'});
      }
      showToasts(toasts);
      this.processErrors(errors);

      // Include toasts and error information in response
      response.toasts = toasts;
      response.hasErrors = errors && Object.keys(errors).length > 0;
    } else {
      this.settingsFormErrors.sections.clear();
      response.hasErrors = false;
    }
    if (result.form_data_json) {
      this.settingsFormData = result.form_data_json;
    }
    const {redirect, redirectMode} = result.response_json;

    if (redirect) {
      response.redirectTarget = redirect;
      response.redirectMode = redirectMode;
    }
    return response;
  }

  /**
   * Loads everything the page shows, for both account-level and per-instance apps. Also the
   * reload after an install or uninstall — the selected CMS instance is preserved, so the same
   * path serves the initial load and every refresh.
   */
  @mobxAction('AppDetailStore<Fetch>')
  private async fetch() {
    // Only blank the card before anything has loaded; a reload updates in place, since the
    // install and uninstall buttons already show their own spinners
    this.loading = !this.detail;
    if (directoryStore.trackerId) {
      await this.fetchInstalledVersion();
    }
    await this.fetchAppDetail();
    this.loading = false;
    this.installing = false;
    this.uninstalling = false;
    if (this.detail) {
      this.getSettingsFormSchema();
      this.getSettingsFormData();
    }
  }

  /**
   * Loads this app's installations into `installations`. The endpoint returns the whole
   * account's installations regardless of scope, so they are filtered to this app here.
   */
  private async loadInstallations(): Promise<AppInstallation[]> {
    const result = await ZipApi.listAppInstallations(directoryStore.trackerId!);
    const appInstalls = (result.installations ?? []).filter((i) => i.app_id === this.appId);
    this.installations = appInstalls;
    return appInstalls;
  }

  private async fetchInstalledVersion() {
    try {
      const appInstalls = await this.loadInstallations();

      // Determine if this app uses per-instance CMS installation
      if (appInstalls.some((i) => i.installation_scope_type === InstallationScopeType.CMS)) {
        this.hasCmsInstallationScope = true;
      }

      if (this.hasCmsInstallationScope) {
        // Only default the dropdown when nothing is chosen yet — reloading after an
        // install/uninstall must leave the user on the instance they just acted on
        if (!this.selectedCmsScope) {
          this.selectedCmsScope = appInstalls[0]?.scope;
        }
      } else {
        // Account-level: classic single installation
        this.accountInstall = appInstalls[0];
        if (!this.requestedVersion) {
          this.version = this.installedVersion;
        }
      }
    } catch (error: any) {
      const message = await getApiErrorMessage(error, 'Unable to load installation status');
      showToasts([{intent: 'danger', message}]);
    }
  }

  /**
   * CMS per-instance apps show metadata for the version installed on the selected instance,
   * falling back to the version a new install gets. When there is none there is no version to
   * show either.
   */
  private async fetchCmsAppDetail() {
    this.latestRunningVersion = await ZipApi.fetchLatestRunningAppVersion(this.appId, directoryStore.trackerId!);
    await this.loadVersionMetadata(this.versionForScope(this.selectedCmsScope));
    this.markLoaded();
    await this.loadCmsProjects();
  }

  /** Publishes the loaded app to the host page so the surrounding Prism chrome stays in sync. */
  private markLoaded() {
    this.status = 200;
    directoryStore.activeAppId = this.appId;
    directoryStore.activeAppVersion = this.version;
    directoryStore.activeAppInstalled = this.installed;
  }

  private async fetchAppDetail() {
    try {
      if (this.hasCmsInstallationScope && directoryStore.trackerId) {
        await this.fetchCmsAppDetail();
        return;
      }

      let app: AppVersion;
      if (this.version) {
        app = (await ZipApi.fetchAppVersion(this.appId, this.version)).app_version;
      } else {
        app = (await ZipApi.fetchApp(this.appId, directoryStore.trackerId)).app;
      }
      if (app.installation_scope_type === InstallationScopeType.CMS) {
        this.hasCmsInstallationScope = true;
      }
      // With no installations there is nothing to read the scope type from until the app itself
      // is loaded, so the CMS setup has to happen here rather than up front. It loads the version
      // for the selected instance, which is not necessarily the one fetched above.
      if (this.hasCmsInstallationScope && directoryStore.trackerId) {
        await this.fetchCmsAppDetail();
        return;
      }
      this.detail = app.metadata;
      this.version = app.id.version;
      this.app_features = app.features || [];
      this.markLoaded();
    } catch (error: any) {
      console.error(error);
      if (error.response && error.response.status) {
        this.status = error.response.status;
      }
      if (this.status !== 404) {
        toaster.show({message: 'There was a problem loading the requested page', intent: Intent.DANGER});
      }
    }
  }

  private async getSettingsFormSchema() {
    try {
      const response = await fetch(this.detail!.settings_url);
      this.settingsFormSchema = yaml.safeLoad(await response.text()) as Schema.Form;
    } catch (error: any) {
      showToasts([{intent: 'danger', message: 'Unable to load settings form'}]);
    }
  }

  private async getSettingsFormData() {
    if (!this.installId) {
      return {sections: []};
    }
    try {
      const result = await ZipApi.fetchFormData(this.installId);
      this.settingsFormData = result.form_data_json;
    } catch (error: any) {
      const message = await getApiErrorMessage(error, 'Unable to load settings data');
      showToasts([{intent: 'danger', message}]);
    }
  }
}
